<script setup lang="ts">
  import { computed, reactive } from "vue";
  import { Shop } from "@src/api";
  import { Modal, Button, RadioButton, Checkbox, Text } from "@ui-kit";

  const { shop, location, store_types } = defineProps<{ shop: Shop; location: string; store_types: string[] }>();
  const emit = defineEmits<{
    "close": [value: void];
    "click:apply": [location: string, store_types: string[]];
  }>();

  const state = reactive<{ location: string; store_types: string[] }>({ location, store_types });
  const shop_store_types = computed(() => Object.entries(shop.store_types).filter(([, value]) => value));

  const isChecked = (key: string) => state.store_types.includes(key);

  const handleLocationClick = (newLocation: string) => {
    if (state.location === newLocation) return;

    state.location = newLocation;
    state.store_types = [];
  };

  const handleStoreTypeClick = (store_type: string) => {
    state.location = "store";

    state.store_types = isChecked(store_type)
      ? state.store_types.filter((type) => type !== store_type)
      : [...state.store_types, store_type];
  };
</script>

<template>
  <Modal close :index="2" data-testid="user-location-modal" @on-close="emit('close')">
    <div class="user-location-modal__header">
      <Text font="body-bold-18">{{ $t("Где вы работаете") }}?</Text>
    </div>
    <div class="user-location-modal__content">
      <div class="section">
        <Text font="body-medium-12" color="secondary">{{ $t("В зале") }}</Text>
        <div class="row" @click="() => handleLocationClick('shopfloor')" data-testid="user-location-modal-shopfloor">
          <Text>{{ $t("Зал") }}</Text>
          <RadioButton value="shopfloor" :is-checked="state.location === 'shopfloor'" />
        </div>
      </div>
      <div class="divider">
        <hr />
        <Text font="body-regular-14" color="secondary">{{ $t("или") }}</Text>
        <hr />
      </div>
      <div class="section">
        <Text font="body-medium-12" color="secondary">{{ $t("На складе") }}</Text>
        <template v-if="shop_store_types.length > 0">
          <div
            v-for="[key] in shop_store_types"
            :key
            class="row"
            @click="() => handleStoreTypeClick(key)"
            :data-testid="`user-location-modal-store-${key}`"
          >
            <Text>{{ key }}</Text>
            <Checkbox :model-value="isChecked(key)" />
          </div>
        </template>
        <div v-else class="row" @click="() => handleLocationClick('store')" data-testid="user-location-modal-store">
          <Text>{{ $t("Склад") }}</Text>
          <RadioButton value="store" :is-checked="state.location === 'store'" />
        </div>
      </div>
    </div>
    <div class="user-location-modal__actions">
      <Button @click="emit('click:apply', state.location, state.store_types)" data-testid="user-location-apply-btn">
        {{ $t("Применить") }}
      </Button>
    </div>
  </Modal>
</template>

<style scoped>
  .user-location-modal__header {
    padding: 3px 0;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .user-location-modal__content {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .row {
      display: flex;
      justify-content: space-between;
      gap: 8px;
      padding: 16px;
      border-radius: 12px;
      background-color: var(--sf-base-background-primary);

      & :deep(.checkbox) {
        pointer-events: none;
      }
    }

    .divider {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 12px;

      hr {
        border-top: 1px dashed var(--sf-base-border-dark);
        flex: 1;
      }
    }

    .section {
      display: flex;
      flex-direction: column;
      gap: 4px;

      & > :deep(.text) {
        margin-bottom: 8px;
      }
    }

    & :deep(.button) {
      width: 100%;
    }
  }

  .user-location-modal__close {
    padding: 16px 0;

    & :deep(.button) {
      width: 100%;
    }
  }

  .user-location-modal__actions {
    & :deep(.button) {
      width: 100%;
    }
  }
</style>
